/**
 * Three.js overlay for duel VFX and 3D card flights. Ortho camera in pixel
 * space, aligned to the Phaser canvas so bursts and flying cards sit on
 * the board.
 */

import * as THREE from 'three'
import { listenWebglLost, webglRendererOptions } from '../webglPhone'
import { currentSleeveUrl, getLocalCardImageUrl } from '../../utils/cardImages'
import { loadCardImage } from '../../utils/cardImageCache'
import { eraseNameUrl } from '../../utils/foilName'
import { isDuelCompactNow } from '../../ui/compactLayout'
import { useDuelPrefsStore } from '../../stores/duelPrefs'
import type { CardFoilId } from '@shared/index'
import {
  ChainStack,
  chainBannerFx,
  type ChainStackHost,
  type ChainStackView,
} from './ChainStack'

const POINT_HAND_URL = '/assets/ui/point-hand.png?v=2'

/** Phones and landscape duel layout. Keeps the effect canvas off a 2× buffer. */
function duelOnPhone(): boolean {
  if (typeof window === 'undefined') return false
  return (
    isDuelCompactNow() || window.matchMedia('(pointer: coarse)').matches
  )
}

/** Reduced or Minimal. Particle bursts stay off; card flights depend on the level. */
function animationsReduced(): boolean {
  try {
    return useDuelPrefsStore().motionLevel !== 'full'
  } catch {
    return false
  }
}

/** Minimal skips 3D card meshes. The board snaps the sprite in instead. */
function animationsMinimal(): boolean {
  try {
    return useDuelPrefsStore().motionLevel === 'minimal'
  } catch {
    return false
  }
}

export type DuelVfxKind =
  | 'summon'
  | 'attack'
  | 'impact'
  | 'spell'
  | 'trap'
  | 'destroy'
  | 'draw'
  | 'flip'
  | 'tornado'
  | 'foilField'
  | 'foilActivate'
  | 'effectFlash'
  | 'lpHit'
  | 'lpHeal'

export interface DuelVfxPoint {
  x: number
  y: number
  x2?: number
  y2?: number
  rarity?: CardFoilId
  /** Monster attribute (LIGHT, DARK, FIRE, …) tints attack / impact. */
  attribute?: string
  /** Card display scale (tribute tornado sized to the sprite). */
  scale?: number
}

export type DuelVfxEase = 'cubicOut' | 'quadOut' | 'quadIn' | 'sineInOut'

/** World-space flight that reprojects through the Phaser camera each frame. */
export interface DuelVfxWorldTrack {
  from: { x: number; y: number }
  to: { x: number; y: number }
  /** World-space bezier peak (same units as from/to). */
  peak?: { x: number; y: number }
  project: (x: number, y: number) => { x: number; y: number }
  /** Overlay-normalized size; re-read so camera zoom keeps the mesh on the sprite. */
  cardSize?: () => { w: number; h: number }
}

export interface DuelVfxFlyCard {
  from: { x: number; y: number }
  to: { x: number; y: number }
  cardId?: string | null
  /** Sleeve for the back face. Defaults to the local player's sleeve. */
  backUrl?: string
  /** Start showing the face (`true`) or the back (`false`). */
  faceUp?: boolean
  /** Yaw-flip to the face around mid-flight. */
  reveal?: boolean
  duration: number
  /** Seconds to sit on `from` before the flight (draw: on the Deck pile). */
  startHold?: number
  settle?: number
  cardW: number
  cardH: number
  startAngle?: number
  peakAngle?: number
  endAngle?: number
  startScale?: number
  endScale?: number
  startAlpha?: number
  endAlpha?: number
  /** Normalized width pulled toward the board center (bezier peak). */
  arc?: number
  yoyo?: boolean
  /** Seconds to hold at the far point before returning (yoyo lunges). */
  yoyoHold?: number
  ease?: DuelVfxEase
  ghosts?: boolean
  lift?: number
  /**
   * Keep the mesh at `to` after the flight until `released` is true. Used to
   * park a spell/trap on the field while its effect animations play.
   */
  holdGate?: { released: boolean }
  /** When set, interpolate in world space and follow camera pan/zoom. */
  worldTrack?: DuelVfxWorldTrack
  /** Ghost Rare bakes pale-silver art onto the mesh. Other foils stay the scan. */
  foil?: CardFoilId
}

/** In-place 3D flip: Set monster / trap turning face-up. */
export interface DuelVfxFlipReveal {
  x: number
  y: number
  cardW: number
  cardH: number
  cardId: string
  backUrl?: string
  defense?: boolean
  /** Flip Summon: rotate DEF → ATK in the same motion as the face reveal. */
  standUp?: boolean
  delay?: number
  flip?: number
  hold?: number
  /** Stay face-up after the hold until `released` (activation on a Set). */
  holdGate?: { released: boolean }
  /** Reproject through the Phaser camera each frame (compact zoom/pan). */
  worldTrack?: {
    x: number
    y: number
    project: (x: number, y: number) => { x: number; y: number }
    cardSize?: () => { w: number; h: number }
  }
  foil?: CardFoilId
}

/** Textured shatter: the card cracks, a white flash covers it, pieces fly. */
export interface DuelVfxShatterCard {
  x: number
  y: number
  cardW: number
  cardH: number
  cardId?: string | null
  backUrl?: string
  faceUp?: boolean
  /** Degrees. 90 for a Defense-position monster. */
  angle?: number
  foil?: CardFoilId
}

/** Soul wisps traveling field → graveyard after a shatter (no card mesh). */
export interface DuelVfxSoulTrail {
  from: { x: number; y: number }
  to: { x: number; y: number }
  duration: number
  /** Normalized width pulled toward the board center (bezier peak). */
  arc?: number
  /** Interpolate in world space and follow camera pan/zoom. */
  worldTrack?: DuelVfxWorldTrack
}

function clamp01(t: number): number {
  return Math.min(1, Math.max(0, t))
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

function degToRad(deg: number): number {
  return deg * (Math.PI / 180)
}

/**
 * Phaser clockwise degrees as a Three.js `rotation.z`. Face-down meshes yaw
 * 180° first (Euler XYZ), which reverses how Z reads on screen — without
 * this, a Set sleeve lands pointing the opposite way from the Phaser sprite.
 */
function phaserClockwiseToThreeZ(clockwiseRad: number, yaw: number): number {
  return Math.cos(yaw) >= 0 ? -clockwiseRad : clockwiseRad
}

/** Mix two 0xRRGGBB colors. */
function mixHex(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255
  const ag = (a >> 8) & 255
  const ab = a & 255
  const br = (b >> 16) & 255
  const bg = (b >> 8) & 255
  const bb = b & 255
  const r = Math.round(ar + (br - ar) * t)
  const g = Math.round(ag + (bg - ag) * t)
  const bl = Math.round(ab + (bb - ab) * t)
  return (r << 16) | (g << 8) | bl
}

function bezier2(a: number, b: number, c: number, t: number): number {
  const u = 1 - t
  return u * u * a + 2 * u * t * b + t * t * c
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / Math.max(1e-5, edge1 - edge0))
  return t * t * (3 - 2 * t)
}

function applyEase(name: DuelVfxEase, t: number): number {
  switch (name) {
    case 'quadOut':
      return 1 - (1 - t) * (1 - t)
    case 'quadIn':
      return t * t
    case 'sineInOut':
      return 0.5 - 0.5 * Math.cos(Math.PI * t)
    default:
      return 1 - (1 - t) ** 3
  }
}

const RARITY_TINT: Partial<Record<CardFoilId, { a: number; b: number }>> = {
  rare: { a: 0xe2e8f0, b: 0xcbd5e1 },
  super: { a: 0xfbbf24, b: 0xfde68a },
  ultra: { a: 0xf59e0b, b: 0xfff7ed },
  ghost: { a: 0xe2e8f0, b: 0xffffff },
  helios: { a: 0xfbbf24, b: 0xff8c14 },
  nightglass: { a: 0x38bdf8, b: 0xa78bfa },
  stormwake: { a: 0xf472b6, b: 0x22d3ee },
}

/** Yu-Gi-Oh attribute colors: core (a) + highlight (b). */
const ATTR_PALETTE: Record<string, { a: number; b: number }> = {
  LIGHT: { a: 0xfde68a, b: 0xfffbeb },
  DARK: { a: 0xa78bfa, b: 0x6d28d9 },
  FIRE: { a: 0xf97316, b: 0xfbbf24 },
  WATER: { a: 0x22d3ee, b: 0x38bdf8 },
  EARTH: { a: 0xd97706, b: 0xf59e0b },
  WIND: { a: 0x4ade80, b: 0xa7f3d0 },
  DIVINE: { a: 0xfacc15, b: 0xfef9c3 },
}

/**
 * Palette for a monster attribute. Unknown / missing falls back to a warm hit.
 */
export function attributePalette(attr?: string): { key: string; a: number; b: number } {
  const key = (attr ?? '').toUpperCase()
  const pal = ATTR_PALETTE[key]
  if (pal) return { key, a: pal.a, b: pal.b }
  return { key: 'NONE', a: 0xfb923c, b: 0xfdba74 }
}

interface Fx {
  update: (dt: number) => boolean
  dispose: () => void
}

let bound: DuelVfx | null = null

export function bindDuelVfx(
  canvas: HTMLCanvasElement,
  getPhaserCanvas: () => HTMLCanvasElement | null,
): DuelVfx | null {
  if (bound && !bound.contextLost && bound.domElement === canvas) {
    bound.retarget(getPhaserCanvas)
    return bound
  }
  // A lost context cannot be recreated on the same element. Chrome paints
  // that canvas white, and it sits over the whole board.
  let target = canvas
  if (bound?.contextLost && bound.domElement === canvas && canvas.parentNode) {
    const next = canvas.cloneNode(false) as HTMLCanvasElement
    next.style.visibility = ''
    canvas.replaceWith(next)
    target = next
  }
  try {
    bound?.dispose()
  } catch {
    /* The previous context is already gone. */
  }
  bound = null
  try {
    bound = new DuelVfx(target, getPhaserCanvas)
    return bound
  } catch {
    target.style.visibility = 'hidden'
    return null
  }
}

/** Drop in-flight effects without giving up the WebGL context. */
export function parkDuelVfx(): void {
  bound?.park()
}

export function unbindDuelVfx(): void {
  bound?.dispose()
  bound = null
}

export function getDuelVfx(): DuelVfx | null {
  return bound
}

export class DuelVfx {
  private readonly renderer: THREE.WebGLRenderer
  private readonly scene: THREE.Scene
  private readonly camera: THREE.OrthographicCamera
  private readonly effects: Fx[] = []
  private getPhaserCanvas: () => HTMLCanvasElement | null
  private readonly texCache = new Map<string, THREE.Texture>()
  private readonly texWait = new Map<string, Array<(t: THREE.Texture) => void>>()
  private readonly texFailed = new Set<string>()
  private backTex: THREE.Texture | null = null
  private raf = 0
  private last = 0
  private disposed = false
  private unlistenGl: (() => void) | null = null
  private w = 1
  private h = 1
  private particleTex: THREE.CanvasTexture | null = null
  private tornadoTex: THREE.CanvasTexture | null = null
  private chain: ChainStack | null = null

  constructor(
    canvas: HTMLCanvasElement,
    getPhaserCanvas: () => HTMLCanvasElement | null,
  ) {
    this.getPhaserCanvas = getPhaserCanvas
    const phone = duelOnPhone()
    this.renderer = new THREE.WebGLRenderer(
      webglRendererOptions(canvas, !phone),
    )
    this.renderer.setPixelRatio(this.overlayPixelRatio())
    this.renderer.setClearColor(0x000000, 0)
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.unlistenGl = listenWebglLost(canvas, this.handleContextLost)
    this.scene = new THREE.Scene()
    this.camera = new THREE.OrthographicCamera(0, 1, 1, 0, -200, 200)
    this.resize()
    window.addEventListener('resize', this.resize)
    this.last = performance.now()
    this.ensureTex(currentSleeveUrl(), (tex) => {
      this.backTex = tex
    })
    this.ensureTex(POINT_HAND_URL, () => undefined)
  }

  /** Draw only while a flight or burst is alive. An idle clear every frame stalled phones. */
  private ensureLoop(): void {
    if (this.disposed || this.raf) return
    this.last = performance.now()
    this.raf = requestAnimationFrame(this.loop)
  }

  play(kind: DuelVfxKind, point: DuelVfxPoint): void {
    if (this.disposed) return
    if (animationsReduced()) {
      this.applyPixelRatio()
      return
    }
    this.resize()
    const a = this.toPx(point.x, point.y)
    const b =
      point.x2 !== undefined && point.y2 !== undefined
        ? this.toPx(point.x2, point.y2)
        : a
    const rarity = point.rarity
    const tint = rarity ? RARITY_TINT[rarity] ?? null : null
    const pal = attributePalette(point.attribute)
    switch (kind) {
      case 'summon':
        this.effects.push(this.rings(a.x, a.y, 0xffe066, 3))
        this.effects.push(this.sparks(a.x, a.y, 0xfff3bf, 28))
        break
      case 'attack':
        this.effects.push(this.bolt(a.x, a.y, b.x, b.y, pal.a))
        this.effects.push(this.beam(a.x, a.y, b.x, b.y, pal.b, pal.key === 'EARTH'))
        break
      case 'impact':
        this.playAttrImpact(a.x, a.y, pal)
        break
      case 'spell':
        this.effects.push(this.rings(a.x, a.y, 0x38bdf8, 4))
        this.effects.push(this.sparks(a.x, a.y, 0x7dd3fc, 24))
        break
      case 'trap':
        this.effects.push(this.rings(a.x, a.y, 0xf87171, 3))
        this.effects.push(this.sparks(a.x, a.y, 0xfca5a5, 20))
        break
      case 'destroy':
        this.effects.push(this.sparks(a.x, a.y, 0xe2e8f0, 40))
        this.effects.push(this.rings(a.x, a.y, 0x94a3b8, 2))
        break
      case 'draw':
        this.effects.push(this.rings(a.x, a.y, 0xa5b4fc, 1))
        this.effects.push(this.sparks(a.x, a.y, 0xc7d2fe, 18))
        break
      case 'flip':
        this.effects.push(this.rings(a.x, a.y, 0xfde68a, 2))
        break
      case 'tornado':
        this.effects.push(this.tornado(a.x, a.y, point.scale ?? 1))
        break
      case 'foilField':
        this.effects.push(this.foilCard(a.x, a.y, tint ?? RARITY_TINT.rare))
        break
      case 'foilActivate':
        this.effects.push(this.foilPrism(a.x, a.y, tint ?? RARITY_TINT.super))
        break
      case 'effectFlash':
        this.effects.push(this.starFlash(a.x, a.y, point.scale ?? 1))
        break
      case 'lpHit':
        this.effects.push(this.lpBurst(a.x, a.y, 0xf87171, false))
        break
      case 'lpHeal':
        this.effects.push(this.lpBurst(a.x, a.y, 0x4ade80, true))
        break
    }
    this.ensureLoop()
  }

  /**
   * Pointing-hand sprite on a card the opponent just chose. The anchor is the
   * fingertip. `project` is read every frame so a camera pan keeps the hand
   * on that card. `h` is the sprite height as a fraction of the overlay.
   */
  showPointHand(
    project: () => { x: number; y: number; h: number },
    lifeSec: number,
  ): void {
    if (this.disposed) return
    this.resize()
    const life = Math.max(0.45, lifeSec)
    this.ensureTex(POINT_HAND_URL, (tex) => {
      if (this.disposed) return
      const img = tex.image as { width?: number; height?: number }
      const aspect =
        img?.width && img?.height ? img.width / img.height : 0.53
      const mat = new THREE.SpriteMaterial({
        map: tex,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      })
      const sprite = new THREE.Sprite(mat)
      sprite.center.set(0.5, 0)
      sprite.position.z = 40
      this.scene.add(sprite)
      let age = 0
      this.effects.push({
        update: (dt) => {
          age += dt
          const n = project()
          const p = this.toPx(n.x, n.y)
          const pxH = Math.max(28, n.h * this.h)
          const bob = Math.sin(age * 16) * Math.min(8, pxH * 0.16)
          sprite.position.set(p.x, this.h - p.y - bob, 40)
          sprite.scale.set(pxH * aspect, pxH, 1)
          const fadeIn = Math.min(1, age / 0.1)
          const fadeOut = age > life - 0.18 ? Math.max(0, (life - age) / 0.18) : 1
          mat.opacity = fadeIn * fadeOut
          return age < life
        },
        dispose: () => {
          this.scene.remove(sprite)
          mat.dispose()
        },
      })
      this.ensureLoop()
    })
  }

  /**
   * Attribute hit: two colored particle sprays that scatter from the card.
   */
  private playAttrImpact(
    x: number,
    y: number,
    pal: { key: string; a: number; b: number },
  ): void {
    this.effects.push(this.flare(x, y, mixHex(pal.b, 0xffffff, 0.55)))
    const a = pal.a
    const b = mixHex(pal.b, 0xffffff, 0.35)
    const up = Math.PI / 2
    switch (pal.key) {
      case 'FIRE':
        this.effects.push(this.spray(x, y, a, 52, {
          size: 36,
          spread: 260,
          lift: 160,
          gravity: 90,
          drag: 0.7,
          life: 0.65,
          cone: 0.7,
          aim: up,
        }))
        this.effects.push(this.spray(x, y, b, 40, {
          size: 22,
          spread: 320,
          lift: 220,
          gravity: 40,
          drag: 0.55,
          life: 0.52,
          cone: 0.85,
          aim: up,
        }))
        break
      case 'WATER':
        this.effects.push(this.spray(x, y, a, 46, {
          size: 32,
          spread: 240,
          lift: 50,
          gravity: 620,
          drag: 0.55,
          life: 0.62,
          cone: 1,
        }))
        this.effects.push(this.spray(x, y, b, 34, {
          size: 20,
          spread: 200,
          gravity: 540,
          drag: 0.45,
          life: 0.55,
          cone: 0.75,
          aim: -up,
        }))
        break
      case 'LIGHT':
        this.effects.push(this.spray(x, y, b, 60, {
          size: 26,
          spread: 420,
          drag: 1.5,
          life: 0.48,
          cone: 1,
        }))
        this.effects.push(this.spray(x, y, a, 32, {
          size: 38,
          spread: 300,
          lift: 40,
          drag: 1.1,
          life: 0.58,
          cone: 1,
        }))
        break
      case 'DARK':
        this.effects.push(this.spray(x, y, a, 48, {
          size: 34,
          spread: 210,
          drag: 1.8,
          life: 0.7,
          cone: 1,
          swirl: 7,
        }))
        this.effects.push(this.spray(x, y, b, 36, {
          size: 20,
          spread: 280,
          drag: 1.4,
          life: 0.55,
          cone: 1,
          swirl: -9,
        }))
        break
      case 'EARTH':
        this.effects.push(this.spray(x, y, a, 32, {
          size: 42,
          spread: 230,
          lift: 90,
          gravity: 820,
          drag: 0.4,
          life: 0.68,
          cone: 0.9,
          aim: up,
        }))
        this.effects.push(this.spray(x, y, b, 40, {
          size: 22,
          spread: 280,
          lift: 40,
          gravity: 680,
          drag: 0.55,
          life: 0.52,
          cone: 1,
        }))
        break
      case 'WIND':
        this.effects.push(this.spray(x, y, a, 50, {
          size: 28,
          spread: 520,
          lift: 20,
          drag: 0.6,
          life: 0.5,
          cone: 1,
          swirl: 11,
        }))
        this.effects.push(this.spray(x, y, b, 38, {
          size: 18,
          spread: 560,
          drag: 0.5,
          life: 0.42,
          cone: 1,
          swirl: -13,
        }))
        break
      case 'DIVINE':
        this.effects.push(this.spray(x, y, a, 52, {
          size: 34,
          spread: 380,
          lift: 70,
          gravity: -40,
          drag: 1.0,
          life: 0.6,
          cone: 1,
        }))
        this.effects.push(this.spray(x, y, b, 40, {
          size: 20,
          spread: 420,
          drag: 1.3,
          life: 0.48,
          cone: 1,
        }))
        break
      default:
        this.effects.push(this.spray(x, y, a, 36, {
          size: 28,
          spread: 320,
          cone: 1,
        }))
        this.effects.push(this.spray(x, y, b, 28, {
          size: 18,
          spread: 360,
          cone: 1,
        }))
    }
  }

  /**
   * Flies a double-sided card mesh from `from` to `to` in normalized Phaser
   * coordinates (0–1). Used for draws, summons, GY, and attack lunges.
   */
  flyCard(opts: DuelVfxFlyCard): void {
    if (this.disposed || animationsMinimal()) return
    if (animationsReduced()) {
      opts = { ...opts, lift: 0, ghosts: false }
    }
    this.resize()
    const from = this.toPx(opts.from.x, opts.from.y)
    const to = this.toPx(opts.to.x, opts.to.y)
    const origin = this.toPx(0, 0)
    const size = this.toPx(opts.cardW, opts.cardH)
    const cw = Math.max(8, Math.abs(size.x - origin.x))
    const ch = Math.max(12, Math.abs(size.y - origin.y))
    const arcPx = opts.arc
      ? Math.abs(this.toPx(opts.arc, 0).x - origin.x)
      : 0
    this.effects.push(this.flyCardFx(from, to, cw, ch, arcPx, opts))
    this.ensureLoop()
  }

  /**
   * Yaw-flips a Set card face-up in place. Flip Summon also stands the card
   * from Defense into Attack in the same motion.
   */
  flipReveal(opts: DuelVfxFlipReveal): void {
    if (this.disposed || animationsMinimal()) return
    this.resize()
    const at = this.toPx(opts.x, opts.y)
    const origin = this.toPx(0, 0)
    const size = this.toPx(opts.cardW, opts.cardH)
    const cw = Math.max(8, Math.abs(size.x - origin.x))
    const ch = Math.max(12, Math.abs(size.y - origin.y))
    this.effects.push(this.flipRevealFx(at.x, at.y, cw, ch, opts))
    this.ensureLoop()
  }

  /**
   * Breaks a card into textured shards under a full-face flash so the Phaser
   * sprite can vanish without a pop. Pieces scatter; the mesh does not fly.
   */
  shatterCard(opts: DuelVfxShatterCard): void {
    if (this.disposed || animationsReduced()) return
    this.resize()
    const at = this.toPx(opts.x, opts.y)
    const origin = this.toPx(0, 0)
    const size = this.toPx(opts.cardW, opts.cardH)
    const cw = Math.max(8, Math.abs(size.x - origin.x))
    const ch = Math.max(12, Math.abs(size.y - origin.y))
    this.effects.push(this.shatterCardFx(at.x, at.y, cw, ch, opts))
    this.ensureLoop()
  }

  /**
   * Pale wisps along a bezier from the shattered slot to the graveyard pile.
   * Lands with a small bloom so the Phaser GY sprite can appear on commit.
   */
  soulTrail(opts: DuelVfxSoulTrail): void {
    if (this.disposed || animationsReduced()) return
    this.resize()
    const from = this.toPx(opts.from.x, opts.from.y)
    const to = this.toPx(opts.to.x, opts.to.y)
    const origin = this.toPx(0, 0)
    const arcPx = opts.arc
      ? Math.abs(this.toPx(opts.arc, 0).x - origin.x)
      : Math.hypot(to.x - from.x, to.y - from.y) * 0.22
    this.effects.push(
      this.soulTrailFx(from, to, arcPx, opts.duration, opts.worldTrack),
    )
    this.ensureLoop()
  }

  /**
   * The chain pile, created on first use. Minimal motion has no pile. The
   * pile lives as one effect and drops itself once its last card is gone.
   */
  chainStack(view: ChainStackView): ChainStack | null {
    if (this.disposed || animationsMinimal()) return null
    this.resize()
    if (this.chain) {
      this.chain.setView(view)
    } else {
      const stack = new ChainStack(this.chainHost(), view)
      this.chain = stack
      this.effects.push({
        update: (dt) => {
          const alive = stack.update(dt)
          if (!alive && this.chain === stack) this.chain = null
          return alive
        },
        dispose: () => {
          if (this.chain === stack) this.chain = null
          stack.dispose()
        },
      })
    }
    this.ensureLoop()
    return this.chain
  }

  /** Fades the pile out (end of resolution, or a resync). */
  clearChainStack(seconds: number): void {
    this.chain?.clear(seconds)
  }

  /** "Chain" banner across the middle of the board. */
  chainBanner(text: string, seconds: number): void {
    if (this.disposed || animationsMinimal()) return
    this.resize()
    this.effects.push(chainBannerFx(this.chainHost(), text, seconds))
    this.ensureLoop()
  }

  private chainHost(): ChainStackHost {
    return {
      scene: this.scene,
      toThree: (nx, ny) => this.overlayToThree(nx, ny),
      pxSize: (nw, nh) => {
        const origin = this.toPx(0, 0)
        const size = this.toPx(nw, nh)
        return { w: Math.abs(size.x - origin.x), h: Math.abs(size.y - origin.y) }
      },
      size: () => ({ w: this.w, h: this.h }),
      makeCard: (cw, ch, cardId, backUrl, foil) =>
        this.makeCardMesh(cw, ch, true, cardId, backUrl, foil),
      reduced: () => animationsReduced(),
    }
  }

  /**
   * Drop GPU card textures and rebuild them from the image cache after
   * the phone backgrounds the app (WebGL context loss).
   */
  restoreGpu(): void {
    if (this.disposed) return
    const urls = [...this.texCache.keys()]
    for (const tex of this.texCache.values()) tex.dispose()
    this.texCache.clear()
    this.texWait.clear()
    this.texFailed.clear()
    this.backTex = null
    this.particleTex?.dispose()
    this.particleTex = null
    this.tornadoTex?.dispose()
    this.tornadoTex = null
    this.ensureTex(currentSleeveUrl(), (tex) => {
      this.backTex = tex
    })
    for (const url of urls) this.ensureTex(url, () => undefined)
  }

  get domElement(): HTMLCanvasElement {
    return this.renderer.domElement
  }

  /** True once the context is gone. A new renderer on that canvas throws. */
  get contextLost(): boolean {
    if (this.disposed) return true
    try {
      const gl = this.renderer.getContext()
      return !gl || gl.isContextLost()
    } catch {
      return true
    }
  }

  /** Point flights at the board that just mounted. The context stays. */
  retarget(getPhaserCanvas: () => HTMLCanvasElement | null): void {
    this.getPhaserCanvas = getPhaserCanvas
    this.renderer.domElement.style.visibility = ''
    this.resize()
  }

  /**
   * Tab is in front again. A frozen 3D flight covers the playmat, and a
   * lost context paints this canvas solid white over the board.
   */
  resumeForeground(dropFlights: boolean): void {
    if (this.disposed) return
    if (this.contextLost) {
      this.renderer.domElement.style.visibility = 'hidden'
      return
    }
    this.renderer.domElement.style.visibility = ''
    if (dropFlights) {
      this.park()
      return
    }
    if (this.raf) {
      cancelAnimationFrame(this.raf)
      this.raf = 0
    }
    try {
      this.renderer.setClearColor(0x000000, 0)
      this.renderer.clear()
    } catch {
      this.renderer.domElement.style.visibility = 'hidden'
      return
    }
    if (this.effects.length > 0) this.ensureLoop()
  }

  /** Clear leftover flights between duels. The canvas must stay usable. */
  park(): void {
    cancelAnimationFrame(this.raf)
    this.raf = 0
    for (const fx of this.effects) fx.dispose()
    this.effects.length = 0
    this.chain = null
    try {
      this.renderer.setClearColor(0x000000, 0)
      this.renderer.clear()
    } catch {
      /* Context already lost. The next bind replaces the canvas. */
    }
  }

  private handleContextLost = (): void => {
    if (this.disposed) return
    this.disposed = true
    cancelAnimationFrame(this.raf)
    this.raf = 0
    this.renderer.domElement.style.visibility = 'hidden'
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    cancelAnimationFrame(this.raf)
    this.unlistenGl?.()
    this.unlistenGl = null
    window.removeEventListener('resize', this.resize)
    for (const fx of this.effects) fx.dispose()
    this.effects.length = 0
    for (const tex of this.texCache.values()) tex.dispose()
    this.texCache.clear()
    this.texWait.clear()
    this.texFailed.clear()
    this.backTex = null
    this.particleTex?.dispose()
    this.particleTex = null
    this.tornadoTex?.dispose()
    this.tornadoTex = null
    this.chain = null
    // Hide first. Losing the context paints the canvas solid white, and
    // this element covers the playmat.
    this.renderer.domElement.style.visibility = 'hidden'
    try {
      this.renderer.forceContextLoss()
    } catch {
      /* Already lost. */
    }
    this.renderer.dispose()
  }

  private toPx(nx: number, ny: number): { x: number; y: number } {
    // Overlay is laid out on the Phaser canvas (see pinOverlayToPhaser).
    return { x: nx * this.w, y: ny * this.h }
  }

  /** Overlay-normalized 0–1 → Three.js pixel space (y up). */
  private overlayToThree(nx: number, ny: number): { x: number; y: number } {
    const p = this.toPx(nx, ny)
    return { x: p.x, y: this.h - p.y }
  }

  private worldToThree(
    track: DuelVfxWorldTrack,
    wx: number,
    wy: number,
  ): { x: number; y: number } {
    const n = track.project(wx, wy)
    return this.overlayToThree(n.x, n.y)
  }

  private trackCardPx(
    track: DuelVfxWorldTrack | undefined,
    fallbackW: number,
    fallbackH: number,
  ): { cw: number; ch: number } {
    if (!track?.cardSize) return { cw: fallbackW, ch: fallbackH }
    const s = track.cardSize()
    const origin = this.toPx(0, 0)
    const size = this.toPx(s.w, s.h)
    return {
      cw: Math.max(8, Math.abs(size.x - origin.x)),
      ch: Math.max(12, Math.abs(size.y - origin.y)),
    }
  }

  /**
   * Sit the Three.js canvas on the FIT Phaser canvas, not the full board-stage.
   * Compact layout letterboxes 1024×768; mapping 0–1 across the stage put
   * draw meshes to the right of the Deck (in the gap toward the HUD).
   */
  private pinOverlayToPhaser(): void {
    const phaser = this.getPhaserCanvas()
    const overlay = this.renderer.domElement
    const parent = overlay.parentElement
    if (!phaser || !parent) return
    const pr = phaser.getBoundingClientRect()
    const br = parent.getBoundingClientRect()
    if (pr.width < 8 || pr.height < 8) return
    const left = Math.round(pr.left - br.left)
    const top = Math.round(pr.top - br.top)
    const w = Math.max(1, Math.round(pr.width))
    const h = Math.max(1, Math.round(pr.height))
    const style = overlay.style
    if (
      style.left === `${left}px` &&
      style.top === `${top}px` &&
      style.width === `${w}px` &&
      style.height === `${h}px`
    ) {
      return
    }
    style.position = 'absolute'
    style.inset = 'unset'
    style.left = `${left}px`
    style.top = `${top}px`
    style.width = `${w}px`
    style.height = `${h}px`
    this.resize()
  }

  private resize = (): void => {
    const el = this.renderer.domElement
    this.w = Math.max(1, el.clientWidth)
    this.h = Math.max(1, el.clientHeight)
    this.camera.right = this.w
    this.camera.top = this.h
    this.camera.updateProjectionMatrix()
    this.applyPixelRatio()
    this.renderer.setSize(this.w, this.h, false)
  }

  /** Phones and Reduce animations stay at 1×. A desktop duel can use up to 2×. */
  private overlayPixelRatio(): number {
    if (duelOnPhone() || animationsReduced()) return 1
    return Math.min(window.devicePixelRatio, 2)
  }

  private applyPixelRatio(): void {
    const ratio = this.overlayPixelRatio()
    if (this.renderer.getPixelRatio() === ratio) return
    this.renderer.setPixelRatio(ratio)
    this.renderer.setSize(this.w, this.h, false)
  }

  private loop = (): void => {
    if (this.disposed) return
    if (this.effects.length === 0) {
      this.renderer.clear()
      this.raf = 0
      return
    }
    this.pinOverlayToPhaser()
    const now = performance.now()
    const dt = Math.min(0.05, (now - this.last) / 1000)
    this.last = now
    for (let i = this.effects.length - 1; i >= 0; i--) {
      if (!this.effects[i]!.update(dt)) {
        this.effects[i]!.dispose()
        this.effects.splice(i, 1)
      }
    }
    if (this.effects.length === 0) {
      this.renderer.clear()
      this.raf = 0
      return
    }
    this.renderer.render(this.scene, this.camera)
    this.raf = requestAnimationFrame(this.loop)
  }

  private rings(x: number, y: number, color: number, count: number): Fx {
    const meshes: THREE.Mesh[] = []
    for (let i = 0; i < count; i++) {
      const geo = new THREE.RingGeometry(6, 10, 40)
      const mat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.9,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
      const mesh = new THREE.Mesh(geo, mat)
      mesh.position.set(x, this.h - y, 0)
      this.scene.add(mesh)
      meshes.push(mesh)
    }
    let age = 0
    const life = 0.55
    return {
      update: (dt) => {
        age += dt
        const t = age / life
        meshes.forEach((mesh, i) => {
          const k = Math.max(0, t - i * 0.08)
          const s = 1 + k * 14
          mesh.scale.setScalar(s)
          const mat = mesh.material as THREE.MeshBasicMaterial
          mat.opacity = Math.max(0, 0.85 - k * 1.1)
        })
        return age < life + 0.25
      },
      dispose: () => {
        for (const mesh of meshes) {
          this.scene.remove(mesh)
          mesh.geometry.dispose()
          ;(mesh.material as THREE.Material).dispose()
        }
      },
    }
  }

  /**
   * Swirling funnel over a tribute: shader-scrolled mist, nested cones, and
   * helical wind ribbons. Built in-engine so it stays sharp at any scale.
   */
  private tornado(x: number, y: number, scale: number): Fx {
    const s = Math.max(0.75, scale)
    const cx = x
    const cy = this.h - y
    const life = 0.92
    const height = 118 * s
    const map = this.tornadoMap()
    const geos: THREE.BufferGeometry[] = []
    const mats: THREE.Material[] = []
    const meshes: THREE.Object3D[] = []

    const addMesh = (mesh: THREE.Mesh, order: number): void => {
      mesh.renderOrder = order
      this.scene.add(mesh)
      meshes.push(mesh)
    }

    const outerGeo = new THREE.CylinderGeometry(
      46 * s,
      16 * s,
      height,
      64,
      20,
      true,
    )
    geos.push(outerGeo)
    const outerMat = this.tornadoMat(map, 1.0)
    mats.push(outerMat)
    const outer = new THREE.Mesh(outerGeo, outerMat)
    outer.position.set(cx, cy + height * 0.28, 0)
    addMesh(outer, 46)

    const innerGeo = new THREE.CylinderGeometry(
      28 * s,
      8 * s,
      height * 0.88,
      64,
      18,
      true,
    )
    geos.push(innerGeo)
    const innerMat = this.tornadoMat(map, 0.72)
    mats.push(innerMat)
    const inner = new THREE.Mesh(innerGeo, innerMat)
    inner.position.set(cx, cy + height * 0.26, 0)
    addMesh(inner, 47)

    const ribbons: THREE.Mesh[] = []
    const ribbonSpecs = [
      { r0: 12 * s, r1: 38 * s, turns: 3.2, w: 3.2 * s, h: height * 0.92 },
      { r0: 9 * s, r1: 32 * s, turns: 4.1, w: 2.4 * s, h: height * 0.86 },
      { r0: 14 * s, r1: 42 * s, turns: 2.5, w: 1.8 * s, h: height * 0.98 },
    ]
    for (const spec of ribbonSpecs) {
      const geo = this.helixStrip(spec.r0, spec.r1, spec.h, spec.turns, spec.w, 96)
      geos.push(geo)
      const mat = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.55,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      })
      mats.push(mat)
      const mesh = new THREE.Mesh(geo, mat)
      mesh.position.set(cx, cy + height * 0.28, 0)
      addMesh(mesh, 48)
      ribbons.push(mesh)
    }

    const coreGeo = new THREE.PlaneGeometry(18 * s, height * 0.9)
    geos.push(coreGeo)
    const coreMat = new THREE.MeshBasicMaterial({
      map: this.dotMap(),
      color: 0xffffff,
      transparent: true,
      opacity: 0.45,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
    mats.push(coreMat)
    const core = new THREE.Mesh(coreGeo, coreMat)
    core.position.set(cx, cy + height * 0.22, 0)
    addMesh(core, 45)

    let age = 0
    return {
      update: (dt) => {
        age += dt
        const u = Math.min(1, age / life)
        const fade =
          u < 0.12 ? u / 0.12 : u > 0.7 ? Math.max(0, 1 - (u - 0.7) / 0.3) : 1
        const grow = 0.78 + u * 0.34
        const wobble = 1 + Math.sin(age * 11) * 0.045
        outer.scale.set(grow * wobble, 1 + u * 0.18, grow * wobble)
        inner.scale.set(grow * 0.92, 1 + u * 0.22, grow * 0.92)
        outer.rotation.y += dt * 4.4
        inner.rotation.y -= dt * 5.8
        outerMat.uniforms.uTime.value = age
        innerMat.uniforms.uTime.value = age * 1.15
        outerMat.uniforms.uAlpha.value = fade * 0.95
        innerMat.uniforms.uAlpha.value = fade * 0.8
        ribbons.forEach((mesh, i) => {
          mesh.rotation.y += dt * (5.2 + i * 1.4) * (i === 1 ? -1 : 1)
          mesh.scale.setScalar(grow)
          const mat = mesh.material as THREE.MeshBasicMaterial
          mat.opacity = fade * (0.42 + i * 0.08)
        })
        core.scale.set(0.85 + Math.sin(age * 8) * 0.12, 1 + u * 0.2, 1)
        coreMat.opacity = fade * 0.5
        return age < life
      },
      dispose: () => {
        for (const mesh of meshes) this.scene.remove(mesh)
        for (const geo of geos) geo.dispose()
        for (const mat of mats) mat.dispose()
      },
    }
  }

  /** Seamless wind streaks for the funnel shader. */
  private tornadoMap(): THREE.CanvasTexture {
    if (this.tornadoTex) return this.tornadoTex
    const size = 256
    const c = document.createElement('canvas')
    c.width = size
    c.height = size
    const ctx = c.getContext('2d')
    if (!ctx) {
      const empty = new THREE.CanvasTexture(c)
      this.tornadoTex = empty
      return empty
    }
    ctx.clearRect(0, 0, size, size)
    for (let i = 0; i < 18; i++) {
      const x0 = (i / 18) * size
      const amp = 18 + (i % 5) * 6
      const wide = 5 + (i % 4) * 3
      ctx.strokeStyle = `rgba(255, 255, 255, ${0.14 + (i % 3) * 0.08})`
      ctx.lineWidth = wide
      ctx.lineCap = 'round'
      for (const ox of [0, size, -size]) {
        ctx.beginPath()
        for (let y = -24; y <= size + 24; y += 3) {
          const x = x0 + Math.sin(y * 0.035 + i * 0.7) * amp + ox
          if (y === -24) ctx.moveTo(x, y)
          else ctx.lineTo(x, y)
        }
        ctx.stroke()
      }
    }
    const tex = new THREE.CanvasTexture(c)
    tex.wrapS = THREE.RepeatWrapping
    tex.wrapT = THREE.RepeatWrapping
    tex.needsUpdate = true
    this.tornadoTex = tex
    return tex
  }

  private tornadoMat(
    map: THREE.Texture,
    gain: number,
  ): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      uniforms: {
        uMap: { value: map },
        uTime: { value: 0 },
        uAlpha: { value: 1 },
        uGain: { value: gain },
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
      vertexShader: `
        varying vec2 vUv;
        uniform float uTime;
        void main() {
          vUv = uv;
          float w = 1.0 + 0.07 * sin(uTime * 9.0 + position.y * 0.09 + uv.x * 6.28318);
          vec3 p = position;
          p.x *= w;
          p.z *= w;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D uMap;
        uniform float uTime;
        uniform float uAlpha;
        uniform float uGain;
        varying vec2 vUv;
        void main() {
          vec2 uvA = vec2(vUv.x * 2.4 + uTime * 0.9, vUv.y * 1.35 - uTime * 0.65);
          vec2 uvB = vec2(vUv.x * -1.8 - uTime * 1.15, vUv.y * 1.1 - uTime * 0.4);
          float mist = max(texture2D(uMap, uvA).r, texture2D(uMap, uvA).a) * 0.75
            + max(texture2D(uMap, uvB).r, texture2D(uMap, uvB).a) * 0.55;
          float arms = 0.5 + 0.5 * sin(vUv.x * 18.8496 - vUv.y * 12.0 - uTime * 10.0);
          arms = smoothstep(0.22, 0.95, arms);
          float veil = smoothstep(0.0, 0.1, vUv.y) * smoothstep(1.0, 0.52, vUv.y);
          float glow = mist * 0.7 + arms * 0.55;
          vec3 col = mix(vec3(0.88, 0.93, 1.0), vec3(1.0, 1.0, 1.0), arms);
          gl_FragColor = vec4(col, glow * veil * uAlpha * uGain);
        }
      `,
    })
  }

  /** Triangle strip along a tapering helix (wind ribbon). */
  private helixStrip(
    r0: number,
    r1: number,
    height: number,
    turns: number,
    halfW: number,
    segs: number,
  ): THREE.BufferGeometry {
    const positions = new Float32Array((segs + 1) * 2 * 3)
    const uvs = new Float32Array((segs + 1) * 2 * 2)
    const indices: number[] = []
    for (let i = 0; i <= segs; i++) {
      const t = i / segs
      const ang = t * turns * Math.PI * 2
      const r = r0 + (r1 - r0) * t
      const y = (t - 0.5) * height
      const c = Math.cos(ang)
      const sn = Math.sin(ang)
      const i2 = i * 2
      positions[i2 * 3] = c * r + c * halfW
      positions[i2 * 3 + 1] = y
      positions[i2 * 3 + 2] = sn * r + sn * halfW
      positions[(i2 + 1) * 3] = c * r - c * halfW
      positions[(i2 + 1) * 3 + 1] = y
      positions[(i2 + 1) * 3 + 2] = sn * r - sn * halfW
      uvs[i2 * 2] = 0
      uvs[i2 * 2 + 1] = t
      uvs[(i2 + 1) * 2] = 1
      uvs[(i2 + 1) * 2 + 1] = t
      if (i < segs) {
        const a = i * 2
        indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2)
      }
    }
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2))
    geo.setIndex(indices)
    return geo
  }

  private sparks(
    x: number,
    y: number,
    color: number,
    count: number,
    opts?: { lift?: number; spread?: number; size?: number },
  ): Fx {
    const spread = opts?.spread ?? 420
    const lift = opts?.lift ?? 0
    const size = (opts?.size ?? 5) * this.renderer.getPixelRatio()
    const positions = new Float32Array(count * 3)
    const velocities = Array.from({ length: count }, () => ({
      x: (Math.random() - 0.5) * spread,
      y: (Math.random() - 0.5) * spread + lift,
    }))
    const geo = new THREE.BufferGeometry()
    const originX = x
    const originY = this.h - y
    for (let i = 0; i < count; i++) {
      positions[i * 3] = originX
      positions[i * 3 + 1] = originY
      positions[i * 3 + 2] = 0
    }
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    const mat = new THREE.PointsMaterial({
      color,
      size,
      map: this.dotMap(),
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: false,
    })
    const points = new THREE.Points(geo, mat)
    this.scene.add(points)
    let age = 0
    const life = 0.48
    return {
      update: (dt) => {
        age += dt
        const attr = geo.getAttribute('position') as THREE.BufferAttribute
        for (let i = 0; i < count; i++) {
          const v = velocities[i]!
          attr.setXYZ(
            i,
            originX + v.x * age,
            originY + v.y * age,
            0,
          )
        }
        attr.needsUpdate = true
        mat.opacity = Math.max(0, 1 - age / life)
        mat.size = size * (1 - age / life)
        return age < life
      },
      dispose: () => {
        this.scene.remove(points)
        geo.dispose()
        mat.dispose()
      },
    }
  }

  /** Soft round sprite used by particle sprays. */
  private dotMap(): THREE.CanvasTexture {
    if (this.particleTex) return this.particleTex
    const c = document.createElement('canvas')
    c.width = 64
    c.height = 64
    const g = c.getContext('2d')
    if (!g) {
      this.particleTex = new THREE.CanvasTexture(c)
      return this.particleTex
    }
    const grd = g.createRadialGradient(32, 32, 0, 32, 32, 32)
    grd.addColorStop(0, 'rgba(255,255,255,1)')
    grd.addColorStop(0.22, 'rgba(255,255,255,1)')
    grd.addColorStop(0.55, 'rgba(255,255,255,0.75)')
    grd.addColorStop(1, 'rgba(255,255,255,0)')
    g.fillStyle = grd
    g.fillRect(0, 0, 64, 64)
    const tex = new THREE.CanvasTexture(c)
    tex.needsUpdate = true
    this.particleTex = tex
    return tex
  }

  /**
   * 4-point sparkle (sharp destello). Unit size; scale the mesh.
   */
  private sparkleGeo(inner = 0.1, outer = 1): THREE.ShapeGeometry {
    const shape = new THREE.Shape()
    const points = 4
    const step = Math.PI / points
    for (let i = 0; i < points * 2; i++) {
      const r = i % 2 === 0 ? outer : inner
      const a = i * step - Math.PI / 2
      const px = Math.cos(a) * r
      const py = Math.sin(a) * r
      if (i === 0) shape.moveTo(px, py)
      else shape.lineTo(px, py)
    }
    shape.closePath()
    return new THREE.ShapeGeometry(shape)
  }

  /**
   * Bright white star destello: grows then shrinks over the card.
   * Parked spell/trap meshes live in a Group with renderOrder 20; Three.js
   * sorts transparent objects by that groupOrder first, so a scene-level
   * mesh (groupOrder 0) always draws behind the card. Wrap the star in a
   * higher-order group so it sits on the face.
   */
  private starFlash(x: number, y: number, scale: number): Fx {
    const wrap = new THREE.Group()
    wrap.renderOrder = 80
    this.scene.add(wrap)
    const make = (rot: number, z: number, opacity: number) => {
      const geo = this.sparkleGeo()
      const mat = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false,
        toneMapped: false,
        side: THREE.DoubleSide,
      })
      const mesh = new THREE.Mesh(geo, mat)
      mesh.position.set(x, this.h - y, z)
      mesh.rotation.z = rot
      mesh.scale.setScalar(0.01)
      wrap.add(mesh)
      return { mesh, geo, mat }
    }
    const cross = make(0, 12, 1)
    const diag = make(Math.PI / 4, 11, 0.85)
    let age = 0
    const life = 0.48
    return {
      update: (dt) => {
        age += dt
        const t = Math.min(1, age / life)
        const pulse = Math.sin(t * Math.PI)
        const grow = pulse * pulse
        cross.mesh.scale.setScalar((10 + grow * 78) * scale)
        diag.mesh.scale.setScalar((8 + grow * 52) * scale)
        cross.mat.opacity = pulse
        diag.mat.opacity = pulse * 0.85
        cross.mesh.rotation.z = t * 0.35
        diag.mesh.rotation.z = Math.PI / 4 - t * 0.28
        return age < life
      },
      dispose: () => {
        this.scene.remove(wrap)
        for (const p of [cross, diag]) {
          p.geo.dispose()
          p.mat.dispose()
        }
      },
    }
  }

  /**
   * Bright additive flash at the hit so particles read as glowing, not dust.
   */
  private flare(x: number, y: number, color: number): Fx {
    const make = (r: number, opacity: number, z: number) => {
      const geo = new THREE.CircleGeometry(1, 24)
      const mat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      })
      const mesh = new THREE.Mesh(geo, mat)
      mesh.position.set(x, this.h - y, z)
      mesh.scale.setScalar(r)
      this.scene.add(mesh)
      return { mesh, geo, mat, r }
    }
    const core = make(16, 1, 5)
    const halo = make(28, 0.7, 4)
    let age = 0
    const life = 0.28
    return {
      update: (dt) => {
        age += dt
        const t = Math.min(1, age / life)
        core.mesh.scale.setScalar(core.r + t * 22)
        halo.mesh.scale.setScalar(halo.r + t * 36)
        core.mat.opacity = Math.max(0, 1 - t)
        halo.mat.opacity = Math.max(0, 0.7 * (1 - t))
        return age < life
      },
      dispose: () => {
        for (const p of [core, halo]) {
          this.scene.remove(p.mesh)
          p.geo.dispose()
          p.mat.dispose()
        }
      },
    }
  }

  /**
   * Colored particles that scatter from a point. `cone` 1 = full burst;
   * smaller cones aim along `aim` (0 = right, π/2 = up).
   */
  private spray(
    x: number,
    y: number,
    color: number,
    count: number,
    opts: {
      size?: number
      spread?: number
      lift?: number
      gravity?: number
      drag?: number
      life?: number
      swirl?: number
      cone?: number
      aim?: number
    } = {},
  ): Fx {
    const size = (opts.size ?? 8) * this.renderer.getPixelRatio()
    const spread = opts.spread ?? 280
    const lift = opts.lift ?? 0
    const gravity = opts.gravity ?? 0
    const drag = opts.drag ?? 1.2
    const life = opts.life ?? 0.55
    const swirl = opts.swirl ?? 0
    const cone = opts.cone ?? 1
    const aim = opts.aim ?? Math.PI / 2
    const ox = x
    const oy = this.h - y
    const pos = new Float32Array(count * 3)
    const vel: { x: number; y: number }[] = []
    const half = cone * Math.PI
    for (let i = 0; i < count; i++) {
      const ang = aim + (Math.random() - 0.5) * 2 * half
      const spd = spread * (0.3 + Math.random() * 0.8)
      vel.push({
        x: Math.cos(ang) * spd,
        y: Math.sin(ang) * spd + lift,
      })
      pos[i * 3] = ox
      pos[i * 3 + 1] = oy
      pos[i * 3 + 2] = 0
    }
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3))
    const mat = new THREE.PointsMaterial({
      color,
      size,
      map: this.dotMap(),
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: false,
      toneMapped: false,
    })
    const points = new THREE.Points(geo, mat)
    this.scene.add(points)
    let age = 0
    return {
      update: (dt) => {
        age += dt
        const t = age / life
        const attr = geo.getAttribute('position') as THREE.BufferAttribute
        const damp = Math.exp(-drag * dt)
        for (let i = 0; i < count; i++) {
          const v = vel[i]!
          if (swirl !== 0) {
            const px = attr.getX(i) - ox
            const py = attr.getY(i) - oy
            v.x += -py * swirl * dt
            v.y += px * swirl * dt
          }
          v.x *= damp
          v.y *= damp
          v.y -= gravity * dt
          attr.setXYZ(i, attr.getX(i) + v.x * dt, attr.getY(i) + v.y * dt, 0)
        }
        attr.needsUpdate = true
        mat.opacity = Math.max(0, 1 - t * t)
        mat.size = size * (1 - t * 0.2)
        return age < life
      },
      dispose: () => {
        this.scene.remove(points)
        geo.dispose()
        mat.dispose()
      },
    }
  }

  private pillar(x: number, y: number, color: number): Fx {
    const h = 96
    const geo = new THREE.PlaneGeometry(18, h)
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.75,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
    const mesh = new THREE.Mesh(geo, mat)
    // Rise from the card's top edge; do not draw through the monster.
    mesh.position.set(x, this.h - y + 46 + h / 2, 0)
    this.scene.add(mesh)
    let age = 0
    const life = 0.62
    return {
      update: (dt) => {
        age += dt
        const t = age / life
        mesh.scale.set(1 + t * 1.4, 1 + t * 0.35, 1)
        mat.opacity = Math.max(0, 0.75 - t)
        return age < life
      },
      dispose: () => {
        this.scene.remove(mesh)
        geo.dispose()
        mat.dispose()
      },
    }
  }

  private orbit(x: number, y: number, colorA: number, colorB: number): Fx {
    const count = 18
    const positions = new Float32Array(count * 3)
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    const mat = new THREE.PointsMaterial({
      color: colorA,
      size: 7,
      map: this.dotMap(),
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: false,
    })
    const points = new THREE.Points(geo, mat)
    this.scene.add(points)
    const ox = x
    const oy = this.h - y
    let age = 0
    const life = 0.85
    return {
      update: (dt) => {
        age += dt
        const t = age / life
        const attr = geo.getAttribute('position') as THREE.BufferAttribute
        const radius = 28 + t * 90
        for (let i = 0; i < count; i++) {
          const ang = (i / count) * Math.PI * 2 + age * 6
          attr.setXYZ(i, ox + Math.cos(ang) * radius, oy + Math.sin(ang) * radius, 0)
        }
        attr.needsUpdate = true
        mat.color.setHex(t > 0.45 ? colorB : colorA)
        mat.opacity = Math.max(0, 1 - t)
        return age < life
      },
      dispose: () => {
        this.scene.remove(points)
        geo.dispose()
        mat.dispose()
      },
    }
  }

  private beam(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color: number,
    heavy = false,
  ): Fx {
    const ax = x1
    const ay = this.h - y1
    const bx = x2
    const by = this.h - y2
    const dx = bx - ax
    const dy = by - ay
    const len = Math.max(8, Math.hypot(dx, dy))
    const geo = new THREE.PlaneGeometry(len, heavy ? 18 : 10)
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
    const mesh = new THREE.Mesh(geo, mat)
    mesh.position.set(ax + dx / 2, ay + dy / 2, 0)
    mesh.rotation.z = Math.atan2(dy, dx)
    this.scene.add(mesh)
    let age = 0
    const life = 0.28
    return {
      update: (dt) => {
        age += dt
        const t = age / life
        mesh.scale.y = 1 + t * 1.8
        mat.opacity = Math.max(0, 0.95 - t)
        return age < life
      },
      dispose: () => {
        this.scene.remove(mesh)
        geo.dispose()
        mat.dispose()
      },
    }
  }

  /** Rainbow foil plate that sits on the card — not combat rings. */
  private foilCard(x: number, y: number, tint: { a: number; b: number }): Fx {
    const mat = this.holoMat(tint.a, tint.b)
    const geo = new THREE.PlaneGeometry(72, 104)
    const mesh = new THREE.Mesh(geo, mat)
    mesh.position.set(x, this.h - y, 2)
    this.scene.add(mesh)
    const gemGeo = new THREE.CircleGeometry(10, 6)
    const gem = new THREE.Mesh(gemGeo, this.holoMat(tint.b, tint.a))
    gem.position.set(x, this.h - y, 3)
    this.scene.add(gem)
    let age = 0
    const life = 0.9
    return {
      update: (dt) => {
        age += dt
        const t = age / life
        mat.uniforms.uTime.value = age
        ;(gem.material as THREE.ShaderMaterial).uniforms.uTime.value = age
        mesh.rotation.z = age * 0.15
        gem.rotation.z = -age * 2.4
        const fade = t < 0.2 ? t / 0.2 : Math.max(0, 1 - (t - 0.2) / 0.8)
        mat.uniforms.uAlpha.value = 0.85 * fade
        ;(gem.material as THREE.ShaderMaterial).uniforms.uAlpha.value =
          0.95 * fade
        gem.scale.setScalar(1 + Math.sin(age * 8) * 0.15)
        return age < life
      },
      dispose: () => {
        this.scene.remove(mesh)
        this.scene.remove(gem)
        geo.dispose()
        gemGeo.dispose()
        mat.dispose()
        ;(gem.material as THREE.Material).dispose()
      },
    }
  }

  /** Vertical prism / aurora used only on foil effect activation. */
  private foilPrism(x: number, y: number, tint: { a: number; b: number }): Fx {
    this.effects.push(this.pillar(x, y, tint.a))
    this.effects.push(this.orbit(x, y, tint.a, tint.b))
    const mat = this.holoMat(tint.a, tint.b)
    const geo = new THREE.PlaneGeometry(28, 260)
    const mesh = new THREE.Mesh(geo, mat)
    mesh.position.set(x, this.h - y + 40, 2)
    this.scene.add(mesh)
    let age = 0
    const life = 0.95
    return {
      update: (dt) => {
        age += dt
        const t = age / life
        mat.uniforms.uTime.value = age * 1.4
        mesh.scale.set(1 + t * 2.2, 1 + t * 0.35, 1)
        mat.uniforms.uAlpha.value = Math.max(0, 0.8 - t)
        return age < life
      },
      dispose: () => {
        this.scene.remove(mesh)
        geo.dispose()
        mat.dispose()
      },
    }
  }

  /** Shot that travels the full attack line (used for direct attacks too). */
  private bolt(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color: number,
  ): Fx {
    const ax = x1
    const ay = this.h - y1
    const bx = x2
    const by = this.h - y2
    const geo = new THREE.CircleGeometry(7, 16)
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
    const mesh = new THREE.Mesh(geo, mat)
    mesh.position.set(ax, ay, 4)
    this.scene.add(mesh)
    let age = 0
    const life = 0.32
    return {
      update: (dt) => {
        age += dt
        const t = Math.min(1, age / life)
        mesh.position.set(ax + (bx - ax) * t, ay + (by - ay) * t, 4)
        mat.opacity = Math.max(0, 1 - t * 0.4)
        mesh.scale.setScalar(1.2 + t * 0.6)
        return age < life
      },
      dispose: () => {
        this.scene.remove(mesh)
        geo.dispose()
        mat.dispose()
      },
    }
  }

  private lpBurst(x: number, y: number, color: number, heal: boolean): Fx {
    this.effects.push(this.rings(x, y, color, heal ? 2 : 3))
    const count = heal ? 16 : 22
    const positions = new Float32Array(count * 3)
    const geo = new THREE.BufferGeometry()
    const ox = x
    const oy = this.h - y
    const dir = heal ? 1 : -1
    const vel = Array.from({ length: count }, () => ({
      x: (Math.random() - 0.5) * 180,
      y: dir * (80 + Math.random() * 160),
    }))
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    const mat = new THREE.PointsMaterial({
      color,
      size: 6,
      map: this.dotMap(),
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: false,
    })
    const points = new THREE.Points(geo, mat)
    this.scene.add(points)
    let age = 0
    const life = 0.7
    return {
      update: (dt) => {
        age += dt
        const attr = geo.getAttribute('position') as THREE.BufferAttribute
        for (let i = 0; i < count; i++) {
          const v = vel[i]!
          attr.setXYZ(i, ox + v.x * age, oy + v.y * age, 0)
        }
        attr.needsUpdate = true
        mat.opacity = Math.max(0, 1 - age / life)
        return age < life
      },
      dispose: () => {
        this.scene.remove(points)
        geo.dispose()
        mat.dispose()
      },
    }
  }

  /**
   * Grid of UV-mapped shards plus a covering flash. Phaser should already
   * have hidden the sprite so this overlay is the only card the player sees.
   */
  private shatterCardFx(
    x: number,
    y: number,
    cw: number,
    ch: number,
    opts: DuelVfxShatterCard,
  ): Fx {
    const ox = x
    const oy = this.h - y
    const rad = -((opts.angle ?? 0) * Math.PI) / 180
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)
    const toWorld = (lx: number, ly: number): { x: number; y: number } => ({
      x: ox + lx * cos - ly * sin,
      y: oy + lx * sin + ly * cos,
    })

    const shardMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 1,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
    })
    this.bindCardFace(shardMat, opts.cardId, opts.faceUp !== false, opts.backUrl, opts.foil)

    const cols = 4
    const rows = 6
    const cellW = cw / cols
    const cellH = ch / rows
    type Shard = {
      mesh: THREE.Mesh
      vx: number
      vy: number
      spin: number
    }
    const shards: Shard[] = []
    const geos: THREE.BufferGeometry[] = []

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const jx = (Math.random() - 0.5) * cellW * 0.22
        const jy = (Math.random() - 0.5) * cellH * 0.22
        const lx = (col + 0.5) * cellW - cw / 2 + jx
        const ly = ch / 2 - (row + 0.5) * cellH + jy
        const geo = new THREE.PlaneGeometry(
          cellW * (0.92 + Math.random() * 0.16),
          cellH * (0.92 + Math.random() * 0.16),
        )
        const u0 = col / cols
        const u1 = (col + 1) / cols
        const v1 = 1 - row / rows
        const v0 = 1 - (row + 1) / rows
        const uv = geo.getAttribute('uv') as THREE.BufferAttribute
        uv.setXY(0, u0, v1)
        uv.setXY(1, u1, v1)
        uv.setXY(2, u0, v0)
        uv.setXY(3, u1, v0)
        uv.needsUpdate = true
        const mesh = new THREE.Mesh(geo, shardMat)
        const at = toWorld(lx, ly)
        mesh.position.set(at.x, at.y, 12)
        mesh.rotation.z = rad
        mesh.renderOrder = 40
        this.scene.add(mesh)
        const dx = at.x - ox
        const dy = at.y - oy
        const len = Math.hypot(dx, dy) || 1
        const speed = 90 + Math.random() * 240
        shards.push({
          mesh,
          vx: (dx / len) * speed + (Math.random() - 0.5) * 70,
          vy: (dy / len) * speed + 50 + Math.random() * 90,
          spin: (Math.random() - 0.5) * 10,
        })
        geos.push(geo)
      }
    }

    const flashGeo = new THREE.PlaneGeometry(cw * 1.18, ch * 1.18)
    const flashMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
    })
    const flash = new THREE.Mesh(flashGeo, flashMat)
    flash.position.set(ox, oy, 14)
    flash.rotation.z = rad
    flash.renderOrder = 42
    this.scene.add(flash)

    const glowGeo = new THREE.PlaneGeometry(cw * 1.45, ch * 1.45)
    const glowMat = new THREE.MeshBasicMaterial({
      color: 0xdbeafe,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
    })
    const glow = new THREE.Mesh(glowGeo, glowMat)
    glow.position.set(ox, oy, 11)
    glow.rotation.z = rad
    glow.renderOrder = 39
    this.scene.add(glow)

    const cracks: THREE.Mesh[] = []
    const crackGeos: THREE.BufferGeometry[] = []
    const crackMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
    })
    for (let i = 0; i < 7; i++) {
      const len = (0.35 + Math.random() * 0.55) * Math.max(cw, ch)
      const geo = new THREE.PlaneGeometry(len, 1.4 + Math.random())
      const mesh = new THREE.Mesh(geo, crackMat)
      const at = toWorld(
        (Math.random() - 0.5) * cw * 0.45,
        (Math.random() - 0.5) * ch * 0.45,
      )
      mesh.position.set(at.x, at.y, 13)
      mesh.rotation.z = rad + (Math.random() - 0.5) * 1.6
      mesh.renderOrder = 41
      this.scene.add(mesh)
      cracks.push(mesh)
      crackGeos.push(geo)
    }

    let age = 0
    const hold = 0.1
    const fly = 0.42
    const life = hold + fly
    return {
      update: (dt) => {
        age += dt
        const flashT = clamp01(age / 0.22)
        flashMat.opacity = flashT < 0.35
          ? 1
          : Math.max(0, 1 - (flashT - 0.35) / 0.65)
        glowMat.opacity = flashMat.opacity * 0.7
        flash.scale.setScalar(1 + flashT * 0.28)
        glow.scale.setScalar(1 + flashT * 0.4)
        crackMat.opacity = age < hold
          ? 0.95
          : Math.max(0, 0.95 - (age - hold) / 0.12)

        if (age > hold) {
          const u = age - hold
          for (const s of shards) {
            s.vy -= 480 * dt
            s.vx *= 0.985
            s.vy *= 0.985
            s.mesh.position.x += s.vx * dt
            s.mesh.position.y += s.vy * dt
            s.mesh.rotation.z += s.spin * dt
            s.mesh.rotation.x += s.spin * 0.35 * dt
          }
          const fade = u < 0.18 ? 1 : Math.max(0, 1 - (u - 0.18) / 0.24)
          shardMat.opacity = fade
        }
        return age < life
      },
      dispose: () => {
        for (const s of shards) this.scene.remove(s.mesh)
        for (const c of cracks) this.scene.remove(c)
        this.scene.remove(flash)
        this.scene.remove(glow)
        for (const g of geos) g.dispose()
        for (const g of crackGeos) g.dispose()
        flashGeo.dispose()
        glowGeo.dispose()
        shardMat.dispose()
        flashMat.dispose()
        glowMat.dispose()
        crackMat.dispose()
      },
    }
  }

  /** Soft soul motes along a bezier, blooming at the graveyard. */
  private soulTrailFx(
    from: { x: number; y: number },
    to: { x: number; y: number },
    arcPx: number,
    duration: number,
    wt?: DuelVfxWorldTrack,
  ): Fx {
    const life = Math.max(0.2, duration)
    const sample = (
      t: number,
    ): { x: number; y: number; overlay: { x: number; y: number } } => {
      if (wt) {
        const peak = wt.peak ?? {
          x: (wt.from.x + wt.to.x) / 2,
          y: (wt.from.y + wt.to.y) / 2,
        }
        const wx = bezier2(wt.from.x, peak.x, wt.to.x, t)
        const wy = bezier2(wt.from.y, peak.y, wt.to.y, t)
        const n = wt.project(wx, wy)
        const p = this.overlayToThree(n.x, n.y)
        const ov = this.toPx(n.x, n.y)
        return { x: p.x, y: p.y, overlay: ov }
      }
      const fx = from.x
      const fy = this.h - from.y
      const tx = to.x
      const ty = this.h - to.y
      const midX = (fx + tx) / 2
      const midY = (fy + ty) / 2
      const towardX = this.w / 2 - midX
      const towardY = this.h / 2 - midY
      const dist = Math.hypot(towardX, towardY) || 1
      const peakX = midX + (towardX / dist) * arcPx
      const peakY = midY + (towardY / dist) * arcPx
      return {
        x: bezier2(fx, peakX, tx, t),
        y: bezier2(fy, peakY, ty, t),
        overlay: {
          x: bezier2(from.x, (from.x + to.x) / 2, to.x, t),
          y: bezier2(from.y, (from.y + to.y) / 2, to.y, t),
        },
      }
    }
    const start = sample(0)
    const end = sample(1)

    const count = 32
    const pos = new Float32Array(count * 3)
    const delay: number[] = []
    const drift: { x: number; y: number }[] = []
    for (let i = 0; i < count; i++) {
      delay.push(Math.random() * 0.2)
      drift.push({
        x: (Math.random() - 0.5) * 22,
        y: (Math.random() - 0.5) * 18,
      })
      pos[i * 3] = start.x
      pos[i * 3 + 1] = start.y
      pos[i * 3 + 2] = 0
    }
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3))
    const mat = new THREE.PointsMaterial({
      color: 0xc4e0ff,
      size: 11,
      map: this.dotMap(),
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: false,
    })
    const points = new THREE.Points(geo, mat)
    points.renderOrder = 44
    this.scene.add(points)

    const headGeo = new THREE.PlaneGeometry(22, 22)
    const headMat = new THREE.MeshBasicMaterial({
      map: this.dotMap(),
      color: 0xf8fafc,
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
    })
    const head = new THREE.Mesh(headGeo, headMat)
    head.position.set(start.x, start.y, 16)
    head.renderOrder = 45
    this.scene.add(head)

    const streakGeo = new THREE.PlaneGeometry(10, 36)
    const streakMat = new THREE.MeshBasicMaterial({
      map: this.dotMap(),
      color: 0x93c5fd,
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
    })
    const streak = new THREE.Mesh(streakGeo, streakMat)
    streak.position.set(start.x, start.y, 15)
    streak.renderOrder = 43
    this.scene.add(streak)

    const bloomGeo = new THREE.PlaneGeometry(28, 28)
    const bloomMat = new THREE.MeshBasicMaterial({
      map: this.dotMap(),
      color: 0xe0f2fe,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
    })
    const bloom = new THREE.Mesh(bloomGeo, bloomMat)
    bloom.position.set(end.x, end.y, 16)
    bloom.renderOrder = 46
    this.scene.add(bloom)

    let age = 0
    let rang = false
    return {
      update: (dt) => {
        age += dt
        const raw = clamp01(age / life)
        const t = applyEase('cubicOut', raw)
        const here = sample(t)
        const ahead = sample(clamp01(t + 0.04))
        const dest = sample(1)
        head.position.set(here.x, here.y, 16)
        head.scale.setScalar(1.05 + Math.sin(age * 14) * 0.12)
        headMat.opacity = 0.25 + (1 - t) * 0.7
        streak.position.set(here.x, here.y, 15)
        streak.rotation.z = Math.atan2(ahead.y - here.y, ahead.x - here.x) - Math.PI / 2
        streakMat.opacity = 0.15 + (1 - t) * 0.5
        streak.scale.set(1, 1.2 + t * 0.6, 1)
        bloom.position.set(dest.x, dest.y, 16)

        const attr = geo.getAttribute('position') as THREE.BufferAttribute
        for (let i = 0; i < count; i++) {
          const local = clamp01((age - delay[i]!) / life)
          const e = applyEase('cubicOut', local)
          const p = sample(e)
          attr.setXYZ(
            i,
            p.x + drift[i]!.x * Math.sin(e * Math.PI),
            p.y + drift[i]!.y * Math.sin(e * Math.PI),
            0,
          )
        }
        attr.needsUpdate = true
        mat.size = 8 + (1 - t) * 8
        mat.opacity = raw < 0.85 ? 0.95 : Math.max(0, 1 - (raw - 0.85) / 0.15)

        if (raw > 0.78) {
          const k = (raw - 0.78) / 0.22
          bloomMat.opacity = k < 0.45 ? k / 0.45 : 1 - (k - 0.45) / 0.55
          bloom.scale.setScalar(0.8 + k * 1.8)
          if (!rang && raw > 0.82) {
            rang = true
            this.effects.push(
              this.rings(dest.overlay.x, dest.overlay.y, 0xbfdbfe, 2),
            )
            this.effects.push(
              this.sparks(dest.overlay.x, dest.overlay.y, 0xe0f2fe, 16, { size: 4 }),
            )
          }
        }
        return age < life + 0.08
      },
      dispose: () => {
        this.scene.remove(points)
        this.scene.remove(head)
        this.scene.remove(streak)
        this.scene.remove(bloom)
        geo.dispose()
        headGeo.dispose()
        streakGeo.dispose()
        bloomGeo.dispose()
        mat.dispose()
        headMat.dispose()
        streakMat.dispose()
        bloomMat.dispose()
      },
    }
  }

  /** Front art when face-up, that seat's sleeve otherwise. Shared cache. */
  private bindCardFace(
    mat: THREE.MeshBasicMaterial,
    cardId?: string | null,
    faceUp = true,
    backUrl?: string,
    foil?: CardFoilId,
  ): void {
    if (faceUp && cardId) {
      this.bindFrontArt(mat, cardId, foil)
      return
    }
    this.bindSleeve(mat, backUrl)
  }

  private bindFrontArt(
    mat: THREE.MeshBasicMaterial,
    cardId: string,
    foil?: CardFoilId,
  ): void {
    const src = getLocalCardImageUrl(cardId)
    const apply = (url: string): void => {
      this.ensureTex(url, (tex) => {
        mat.map = tex
        mat.color.setHex(0xffffff)
        mat.needsUpdate = true
      })
    }
    if (foil !== 'ghost') {
      apply(src)
      return
    }
    apply(src)
    void eraseNameUrl(src, true).then((url) => {
      if (this.disposed) return
      apply(url)
    })
  }

  private bindSleeve(mat: THREE.MeshBasicMaterial, backUrl?: string): void {
    const url = backUrl || currentSleeveUrl()
    this.ensureTex(url, (tex) => {
      mat.map = tex
      mat.color.setHex(0xffffff)
      mat.needsUpdate = true
    })
  }

  private ensureTex(url: string, apply: (t: THREE.Texture) => void): void {
    const hit = this.texCache.get(url)
    if (hit) {
      apply(hit)
      return
    }
    if (this.texFailed.has(url)) return
    const waiting = this.texWait.get(url)
    if (waiting) {
      waiting.push(apply)
      return
    }
    this.texWait.set(url, [apply])
    void loadCardImage(url)
      .then((img) => {
        if (this.disposed) {
          this.texWait.delete(url)
          return
        }
        const tex = new THREE.Texture(img)
        tex.colorSpace = THREE.SRGBColorSpace
        tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy()
        tex.needsUpdate = true
        this.texCache.set(url, tex)
        const cbs = this.texWait.get(url) ?? []
        this.texWait.delete(url)
        for (const cb of cbs) cb(tex)
      })
      .catch(() => {
        this.texWait.delete(url)
        this.texFailed.add(url)
      })
  }

  private makeCardMesh(
    cw: number,
    ch: number,
    faceUp: boolean,
    cardId?: string | null,
    backUrl?: string,
    foil?: CardFoilId,
  ): {
    group: THREE.Group
    front: THREE.Mesh
    back: THREE.Mesh
    frontMat: THREE.MeshBasicMaterial
    backMat: THREE.MeshBasicMaterial
    edgeMat: THREE.MeshBasicMaterial
    showFace: (yaw: number) => void
    dispose: () => void
  } {
    const thick = Math.max(2, cw * 0.03)
    const frontGeo = new THREE.PlaneGeometry(cw, ch)
    const backGeo = new THREE.PlaneGeometry(cw, ch)
    const edgeGeo = new THREE.BoxGeometry(cw, ch, thick)
    const frontMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 1,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
    })
    const backMat = new THREE.MeshBasicMaterial({
      color: 0x1b2a44,
      transparent: true,
      opacity: 1,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
    })
    const edgeMat = new THREE.MeshBasicMaterial({
      color: 0x120e18,
      transparent: true,
      opacity: 1,
      depthWrite: false,
      depthTest: false,
    })
    this.bindSleeve(backMat, backUrl)
    if (cardId) {
      this.bindFrontArt(frontMat, cardId, foil)
    } else {
      frontMat.color.setHex(0x223344)
    }
    const front = new THREE.Mesh(frontGeo, frontMat)
    const back = new THREE.Mesh(backGeo, backMat)
    const edge = new THREE.Mesh(edgeGeo, edgeMat)
    front.position.z = thick / 2
    back.position.z = -thick / 2
    back.rotation.y = Math.PI
    const group = new THREE.Group()
    group.add(edge, front, back)
    const showFace = (yaw: number): void => {
      group.rotation.y = yaw
      const facing = Math.cos(yaw)
      front.visible = facing > 0.04
      back.visible = facing < -0.04
    }
    showFace(faceUp ? 0 : Math.PI)
    group.renderOrder = 20
    return {
      group,
      front,
      back,
      frontMat,
      backMat,
      edgeMat,
      showFace,
      dispose: () => {
        frontGeo.dispose()
        backGeo.dispose()
        edgeGeo.dispose()
        frontMat.dispose()
        backMat.dispose()
        edgeMat.dispose()
      },
    }
  }

  private flyCardFx(
    from: { x: number; y: number },
    to: { x: number; y: number },
    cw: number,
    ch: number,
    arcPx: number,
    opts: DuelVfxFlyCard,
  ): Fx {
    const fx = from.x
    const fy = this.h - from.y
    const tx = to.x
    const ty = this.h - to.y
    const midX = (fx + tx) / 2
    const midY = (fy + ty) / 2
    const towardX = this.w / 2 - midX
    const towardY = this.h / 2 - midY
    const dist = Math.hypot(towardX, towardY) || 1
    const peakX = midX + (towardX / dist) * arcPx
    const peakY = midY + (towardY / dist) * arcPx
    const visStart = degToRad(opts.startAngle ?? 0)
    const visPeak = degToRad(opts.peakAngle ?? 0)
    const visEnd = degToRad(opts.endAngle ?? 0)
    const startScale = opts.startScale ?? 1
    const endScale = opts.endScale ?? 1
    const startAlpha = opts.startAlpha ?? 1
    const endAlpha = opts.endAlpha ?? 1
    const faceUp = opts.faceUp ?? false
    const reveal = opts.reveal ?? false
    const duration = Math.max(0.05, opts.duration)
    const startHold = Math.max(0, opts.startHold ?? 0)
    const settle = opts.settle ?? 0
    const yoyo = opts.yoyo ?? false
    const yoyoHold = yoyo ? Math.max(0, opts.yoyoHold ?? 0) : 0
    const liftAmt = opts.lift ?? (arcPx > 0 ? 1 : 0.3)
    const easeName = opts.ease ?? 'cubicOut'
    const card = this.makeCardMesh(cw, ch, faceUp, opts.cardId, opts.backUrl, opts.foil)
    const group = card.group
    const wt = opts.worldTrack
    const startPos = wt
      ? this.worldToThree(wt, wt.from.x, wt.from.y)
      : { x: fx, y: fy }
    group.position.set(startPos.x, startPos.y, 8)
    group.rotation.z = phaserClockwiseToThreeZ(
      visStart,
      faceUp ? 0 : Math.PI,
    )
    group.scale.setScalar(startScale)
    this.scene.add(group)

    const shadowGeo = new THREE.CircleGeometry(1, 24)
    const shadowMat = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.32,
      depthWrite: false,
      depthTest: false,
    })
    const shadow = new THREE.Mesh(shadowGeo, shadowMat)
    shadow.scale.set(cw * 0.36, 7, 1)
    shadow.position.set(startPos.x + 4, startPos.y - 12, 2)
    shadow.renderOrder = 14
    this.scene.add(shadow)

    let age = 0
    let lastGhost = 0
    const motionLife = yoyo ? duration * 2 + yoyoHold : duration + settle
    const life = motionLife + startHold
    return {
      update: (dt) => {
        const flightAge = age - startHold
        let raw: number
        if (flightAge <= 0) {
          raw = 0
        } else if (yoyo) {
          if (flightAge <= duration) {
            raw = clamp01(flightAge / duration)
          } else if (flightAge <= duration + yoyoHold) {
            raw = 1
          } else {
            raw = clamp01(1 - (flightAge - duration - yoyoHold) / duration)
          }
        } else {
          raw = clamp01(flightAge / duration)
        }
        const t = applyEase(easeName, raw)
        // Yoyo lunges stay lifted at the far point (the hit); other flights hop.
        const lift = yoyo
          ? Math.sin(t * Math.PI * 0.5) * liftAmt
          : Math.sin(Math.min(1, t) * Math.PI) * liftAmt
        let scale = lerp(startScale, endScale, t) + lift * 0.14
        let visAng = bezier2(visStart, visPeak, visEnd, t)
        let tilt = lift * 0.4
        const parked =
          !yoyo &&
          flightAge > 0 &&
          ((!!opts.holdGate && flightAge >= duration) ||
            (flightAge > duration && settle > 0))
        if (parked) {
          visAng = visEnd
          tilt = 0
          if (opts.holdGate) {
            scale = endScale
          } else {
            const s = clamp01((flightAge - duration) / settle)
            scale = endScale * (1 + Math.sin(s * Math.PI) * 0.06)
          }
        }
        let posX: number
        let posY: number
        if (wt) {
          const peak = wt.peak ?? {
            x: (wt.from.x + wt.to.x) / 2,
            y: (wt.from.y + wt.to.y) / 2,
          }
          const wx = parked
            ? wt.to.x
            : bezier2(wt.from.x, peak.x, wt.to.x, t)
          const wy = parked
            ? wt.to.y
            : bezier2(wt.from.y, peak.y, wt.to.y, t)
          const p = this.worldToThree(wt, wx, wy)
          posX = p.x
          posY = p.y
        } else {
          posX = parked ? tx : bezier2(fx, peakX, tx, t)
          posY = parked ? ty : bezier2(fy, peakY, ty, t)
        }
        const nowSize = this.trackCardPx(wt, cw, ch)
        const sizeMul = nowSize.cw / cw
        let yaw = faceUp ? 0 : Math.PI
        if (reveal) yaw = lerp(Math.PI, 0, smoothstep(0.38, 0.62, t))
        const ang = phaserClockwiseToThreeZ(visAng, yaw)
        group.position.set(posX, posY, 8)
        group.rotation.z = ang
        group.rotation.x = tilt
        group.scale.setScalar(scale * sizeMul)
        card.showFace(yaw)
        const alpha = lerp(startAlpha, endAlpha, t)
        card.frontMat.opacity = alpha
        card.backMat.opacity = alpha
        card.edgeMat.opacity = alpha
        shadow.position.set(posX + 4, posY - 12 - lift * 16, 2)
        shadow.scale.set(
          nowSize.cw * 0.36 * (1 + lift * 0.45),
          7 * (1 + lift * 0.45),
          1,
        )
        shadowMat.opacity = 0.32 * (1 - lift * 0.45) * alpha
        if (
          opts.ghosts &&
          t - lastGhost > 0.22 &&
          t < 0.85 &&
          flightAge > 0 &&
          flightAge <= duration
        ) {
          lastGhost = t
          this.effects.push(
            this.ghostCard(
              posX,
              posY,
              ang,
              scale,
              nowSize.cw,
              nowSize.ch,
              card.backMat.map,
            ),
          )
        }
        if (opts.holdGate && !yoyo) {
          const keep = !(opts.holdGate.released && flightAge >= duration)
          age += dt
          return keep
        }
        const keep = age < life
        age += dt
        return keep
      },
      dispose: () => {
        this.scene.remove(group)
        this.scene.remove(shadow)
        card.dispose()
        shadowGeo.dispose()
        shadowMat.dispose()
      },
    }
  }

  private flipRevealFx(
    x: number,
    y: number,
    cw: number,
    ch: number,
    opts: DuelVfxFlipReveal,
  ): Fx {
    const py = this.h - y
    const delay = opts.delay ?? 0
    const flip = Math.max(0.08, opts.flip ?? 0.4)
    const hold = opts.hold ?? 0.38
    const startYaw = Math.PI
    const startVis = opts.defense || opts.standUp ? Math.PI / 2 : 0
    const card = this.makeCardMesh(cw, ch, false, opts.cardId, opts.backUrl, opts.foil)
    const group = card.group
    const wt0 = opts.worldTrack
    if (wt0) {
      const n = wt0.project(wt0.x, wt0.y)
      const p = this.overlayToThree(n.x, n.y)
      group.position.set(p.x, p.y, 8)
    } else {
      group.position.set(x, py, 8)
    }
    group.rotation.z = phaserClockwiseToThreeZ(startVis, startYaw)
    card.showFace(startYaw)
    group.visible = delay <= 0
    this.scene.add(group)
    let age = -delay
    return {
      update: (dt) => {
        age += dt
        if (age < 0) {
          group.visible = false
          return true
        }
        group.visible = true
        if (age < flip) {
          const t = applyEase('sineInOut', clamp01(age / flip))
          const yaw = lerp(Math.PI, 0, t)
          const vis = opts.standUp
            ? lerp(Math.PI / 2, 0, t)
            : opts.defense
              ? Math.PI / 2
              : 0
          card.showFace(yaw)
          group.rotation.z = phaserClockwiseToThreeZ(vis, yaw)
          group.rotation.x = Math.sin(t * Math.PI) * 0.18
        } else {
          const vis = opts.standUp ? 0 : opts.defense ? Math.PI / 2 : 0
          card.showFace(0)
          group.rotation.z = phaserClockwiseToThreeZ(vis, 0)
          group.rotation.x = 0
        }
        const wt = opts.worldTrack
        if (wt) {
          const n = wt.project(wt.x, wt.y)
          const p = this.overlayToThree(n.x, n.y)
          group.position.set(p.x, p.y, 8)
          const now = this.trackCardPx(
            {
              from: { x: wt.x, y: wt.y },
              to: { x: wt.x, y: wt.y },
              project: wt.project,
              cardSize: wt.cardSize,
            },
            cw,
            ch,
          )
          group.scale.setScalar(now.cw / cw)
        }
        if (opts.holdGate) {
          return !(opts.holdGate.released && age >= flip)
        }
        return age < flip + hold
      },
      dispose: () => {
        this.scene.remove(group)
        card.dispose()
      },
    }
  }

  private ghostCard(
    x: number,
    y: number,
    angle: number,
    scale: number,
    cw: number,
    ch: number,
    backMap: THREE.Texture | null,
  ): Fx {
    const map = backMap
    const geo = new THREE.PlaneGeometry(cw, ch)
    const mat = new THREE.MeshBasicMaterial({
      map,
      color: map ? 0xffffff : 0x1b2a44,
      transparent: true,
      opacity: 0.2,
      depthWrite: false,
      depthTest: false,
    })
    const mesh = new THREE.Mesh(geo, mat)
    mesh.position.set(x, y, 6)
    mesh.rotation.z = angle
    mesh.scale.setScalar(scale)
    mesh.renderOrder = 15
    this.scene.add(mesh)
    let age = 0
    const life = 0.2
    return {
      update: (dt) => {
        age += dt
        const k = age / life
        mat.opacity = 0.2 * (1 - k)
        mesh.scale.setScalar(scale * (1 - k * 0.14))
        return age < life
      },
      dispose: () => {
        this.scene.remove(mesh)
        geo.dispose()
        mat.dispose()
      },
    }
  }

  private holoMat(colorA: number, colorB: number): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uAlpha: { value: 0.85 },
        uA: { value: new THREE.Color(colorA) },
        uB: { value: new THREE.Color(colorB) },
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uTime;
        uniform float uAlpha;
        uniform vec3 uA;
        uniform vec3 uB;
        varying vec2 vUv;
        void main() {
          float sweep = fract(vUv.x * 0.9 + vUv.y * 0.35 + uTime * 0.7);
          vec3 rainbow = vec3(
            0.5 + 0.5 * sin(sweep * 6.28318 + 0.0),
            0.5 + 0.5 * sin(sweep * 6.28318 + 2.094),
            0.5 + 0.5 * sin(sweep * 6.28318 + 4.188)
          );
          vec3 col = mix(uA, uB, sweep) + rainbow * 0.4;
          float edge = smoothstep(0.0, 0.07, vUv.x) * smoothstep(0.0, 0.07, vUv.y)
            * smoothstep(1.0, 0.93, vUv.x) * smoothstep(1.0, 0.93, vUv.y);
          gl_FragColor = vec4(col, uAlpha * edge);
        }
      `,
    })
  }
}
